import { PersistentMessagesContext } from 'src/components/persistent-messages';
import { useTranslate } from 'src/locales';
import {
  fetchRemoteBuildId,
  getCurrentBuildId,
  isNewerBuild,
} from 'src/utils/app-update';

import { useContext, useEffect, useRef } from 'react';

// ----------------------------------------------------------------------

// How often to re-check for a new release while the tab stays open.
const CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
// Small delay before the first check so it never competes with initial boot.
const INITIAL_CHECK_DELAY_MS = 15 * 1000;

/**
 * Polls `version.json` and, when a newer release is detected, surfaces a
 * non-blocking "new version available" banner via the persistent messages
 * provider. Refreshing is the user's choice — durable assets on R2 keep the
 * current tab working until then. Renders nothing.
 */
export function AppUpdateChecker() {
  const { actions } = useContext(PersistentMessagesContext);
  const { t } = useTranslate('appUpdate');

  // Refs so the effect runs exactly once: re-subscribing timers/listeners on
  // every language change (t identity) or provider re-render would needlessly
  // reset the poll cadence.
  const tRef = useRef(t);
  tRef.current = t;
  const addMessageRef = useRef(actions.addMessage);
  addMessageRef.current = actions.addMessage;

  useEffect(() => {
    const currentBuildId = getCurrentBuildId();
    // In dev no version.json is emitted, so fetchRemoteBuildId returns null and
    // no banner ever shows — the periodic poll is harmless there.
    if (!currentBuildId) {
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    const check = async () => {
      const remoteBuildId = await fetchRemoteBuildId(controller.signal);
      if (cancelled || !isNewerBuild(currentBuildId, remoteBuildId)) {
        return;
      }

      // Build id in the message id → a given release prompts once (dismissal
      // persists), while the next release prompts again.
      addMessageRef.current({
        id: `app-update-${remoteBuildId}`,
        type: 'info',
        title: tRef.current('title'),
        action: {
          label: tRef.current('action'),
          onClick: () => {
            window.location.reload();
          },
        },
      });
    };

    const checkOnVisible = () => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };
    const checkOnOnline = () => {
      void check();
    };

    const initialTimeout = window.setTimeout(() => {
      void check();
    }, INITIAL_CHECK_DELAY_MS);
    const interval = window.setInterval(() => {
      void check();
    }, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', checkOnVisible);
    window.addEventListener('online', checkOnOnline);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(initialTimeout);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', checkOnVisible);
      window.removeEventListener('online', checkOnOnline);
    };
  }, []);

  return null;
}
